import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { DesignSystemToken } from 'designSystem/types';
import RecipeSpecimens from './RecipeSpecimens';
import TypeScale from './TypeScale';
import WeightScale from './WeightScale';
import LineHeightScale from './LineHeightScale';
import * as Base from '../../styles';
import * as S from './styles';

interface TypographySectionProps {
	tokens: DesignSystemToken[];
	previewText: string;
	dark: boolean;
}

const TypographySection = ({ tokens, previewText, dark }: TypographySectionProps) => {
	const fonts = useSelector((r: RootReducer) => r.mode.designSystemContent?.config?.fonts);
	const typeRecipeCount = useSelector(
		(r: RootReducer) =>
			r.mode.designSystemContent?.styles?.recipes?.byDomain['type']?.length ?? 0
	);

	// Primary font family string for token scale previews
	const primaryFont = useMemo(() => {
		if (!fonts || fonts.length === 0) return 'inherit';
		const f = fonts[0];
		return `"${f.fontFamily}", ${f.fallback ?? 'sans-serif'}`;
	}, [fonts]);

	// Group typography tokens by subcategory
	const { fontSizes, fontWeights, lineHeights } = useMemo(() => {
		const sizes: DesignSystemToken[] = [];
		const weights: DesignSystemToken[] = [];
		const heights: DesignSystemToken[] = [];

		for (const t of tokens) {
			switch (t.subcategory) {
				case 'fontSizes':
					sizes.push(t);
					break;
				case 'fontWeights':
					weights.push(t);
					break;
				case 'lineHeights':
					heights.push(t);
					break;
			}
		}

		return { fontSizes: sizes, fontWeights: weights, lineHeights: heights };
	}, [tokens]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<S.Base>
			{typeRecipeCount > 0 && (
				<>
					<Base.SectionDivider>Type Styles</Base.SectionDivider>
					<RecipeSpecimens previewText={previewText} dark={dark} />
				</>
			)}

			{fontSizes.length > 0 && (
				<>
					<Base.SectionDivider>Font Sizes</Base.SectionDivider>
					<TypeScale
						tokens={fontSizes}
						previewText={previewText}
						dark={dark}
						primaryFont={primaryFont}
					/>
				</>
			)}

			{fontWeights.length > 0 && (
				<>
					<Base.SectionDivider>Font Weights</Base.SectionDivider>
					<WeightScale tokens={fontWeights} dark={dark} primaryFont={primaryFont} />
				</>
			)}

			{lineHeights.length > 0 && (
				<>
					<Base.SectionDivider>Line Heights</Base.SectionDivider>
					<LineHeightScale tokens={lineHeights} dark={dark} primaryFont={primaryFont} />
				</>
			)}
		</S.Base>
	);
};

export default TypographySection;
